import React from "react";
import "./HomeCSS/accountDeletion.css";

const AccountDeletion = () => {
  return (
    <div className="accountDeletionPage">
      <div className="adp-header">
        <h1>Account Deletion - Realtor Organizer</h1>
        <p>How to Delete Your Account and Manage Your Data</p>
      </div>

      <div className="adp-container">
        <p className="adp-intro">
          Realtor Organizer ("we", "us", "our") respects your right to delete
          your account and control your personal data. This page explains how to
          request account deletion and what happens to your data when you delete
          your account.
        </p>

        <div className="adp-warning">
          <strong>Important:</strong> Account deletion is permanent and cannot be
          undone. Once your account is deleted, you will lose access to your
          profile, contacts, properties, subscriptions, and account history.
          Active subscriptions will be cancelled immediately.
        </div>

        <div className="adp-section">
          <h2>Steps to Delete Your Account</h2>
          <p>
            Follow these steps to delete your Realtor Organizer account:
          </p>
          <ol>
            <li>
              <strong>Log in to the app:</strong> Open Realtor Organizer and sign
              in with your email and password (or sign in with Google, Apple,
              etc., if applicable).
            </li>
            <li>
              <strong>Go to Profile:</strong> From the bottom navigation, tap{" "}
              <strong>Profile</strong>.
            </li>
            <li>
              <strong>Open Settings:</strong> Tap the <strong>Settings</strong>{" "}
              icon in the top right corner.
            </li>
            <li>
              <strong>Tap "Delete User":</strong> Find the{" "}
              <strong>Delete User</strong> option. Review the warning messages
              and confirm that you want to permanently delete your account.
            </li>
          </ol>
        </div>

        <div className="adp-section">
          <h2>Can't Access Your Account?</h2>
          <p>
            If you're unable to log in to your account or need assistance with
            account deletion, please contact our support team.
          </p>
          <a href="/contactus" className="adp-contact-link">
            Contact Support
          </a>
        </div>

        <div className="adp-section">
          <h2>What Data Is Deleted or Retained?</h2>

          <h3>Data That Is Deleted or Anonymized</h3>
          <p>
            When you delete your account, the following information is
            immediately deleted or anonymized:
          </p>
          <ul>
            <li>
              <strong>Personal Information:</strong> Your name, email address,
              and profile details are anonymized.
            </li>
            <li>
              <strong>Profile Picture:</strong> Your avatar/profile picture is
              permanently deleted.
            </li>
            <li>
              <strong>Active Subscriptions:</strong> All active subscriptions are
              immediately cancelled.
            </li>
            <li>
              <strong>Subscription Status:</strong> Subscription type and status
              are removed from your account.
            </li>
            <li>
              <strong>Account Preferences:</strong> Saved preferences and
              settings are deleted.
            </li>
            <li>
              <strong>Account-Linked Data:</strong> Contacts, properties,
              catalogs, and other linked data are removed or anonymized.
            </li>
          </ul>

          <h3>Data That May Be Retained</h3>
          <p>
            For legal, compliance, and system integrity purposes, the following
            data may be retained in anonymized form:
          </p>
          <ul>
            <li>
              <strong>Account Record:</strong> An anonymized account record may
              be retained for system integrity and to prevent abuse.
            </li>
            <li>
              <strong>Transaction Records:</strong> Payment and subscription
              records may be retained as required by law (e.g. tax or financial
              regulations).
            </li>
            <li>
              <strong>Service Logs:</strong> System logs and analytics data may
              be retained in anonymized or aggregated form.
            </li>
          </ul>

          <div className="adp-note">
            <strong>Note:</strong> All retained data is anonymized and cannot be
            linked back to your identity. We do not use retained data for
            marketing or any purpose other than the lawful reasons stated above.
          </div>
        </div>

        <div className="adp-section">
          <h2>Important Information</h2>
          <ul>
            <li>
              <strong>Subscription Cancellation:</strong> Deleting your account
              will immediately cancel all active subscriptions. You will lose
              access to premium features.
            </li>
            <li>
              <strong>Refunds:</strong> Account deletion does not automatically
              trigger refunds. Contact support before deleting if you have refund
              questions.
            </li>
            <li>
              <strong>Third-Party Services:</strong> If you signed in using
              Google, Apple, or another provider, you may need to manage those
              permissions in that provider's settings separately.
            </li>
            <li>
              <strong>Recovery:</strong> Once your account is deleted, it cannot
              be recovered. All your data, preferences, and access will be
              permanently lost.
            </li>
          </ul>
        </div>
      </div>

      <div className="adp-footer">
        <span>&copy; {new Date().getFullYear()} Realtor Organizer. All rights reserved.</span>
      </div>
    </div>
  );
};

export default AccountDeletion;
